<template>
    <div v-if="component" class="component-information__container inline-flex align-center justify-between"
         @click.stop="toggleExpansion"
         :ref="'infoIcon_' + component.cId"
    >
        <Icon name="info"
              aria-label="Show component information"
              :color="isHovered('info-' + component.cId) ? '#696969' : '#999'"
              @mouseenter="setHover('info-' + component.cId)"
              @mouseleave="clearHover"
              :size="'2xl'"
              class="info-icon"></Icon>
    </div>
    <div v-if="component"
         v-show="showComponentInfo"
         class="component-information"
         :class="{ expanded: showComponentInfo }"
         @click.stop
         :ref="'infoPanel_' + component.cId"
    >
        <div class="flex justify-between header align-center">
            <h3>{{ component.cTitle }}</h3>
            <button aria-label="close"
                    @click.stop="toggleExpansion">
                <Icon key="close"
                      name="close"
                      :size="'2xl'"
                      :color="isHovered('info-' + component.cId) ? '#696969' : '#999'"
                      @mouseenter="setHover('info-' + component.cId)"
                      @mouseleave="clearHover"></Icon>
            </button>
        </div>
        <div class="component-information__div description" v-if="component.cInfo">
            <span class="label">Description: </span>
            <div class="value">
                <component :is="componentInformation.cId" :key="component.cId + '-' + component.cLastModify"></component>
            </div>
        </div>
        <div class="component-information__div">
            <span class="label">Component ID:</span>
            <div class="info-row">
                <span class="cId value">{{ component.cId }}</span>
                <div class="copy-wrapper">
                    <transition name="fade" mode="out-in">
                        <Icon v-if="copiedId !== component.cId"
                              key="copy"
                              name="copy"
                              size="xl"
                              :color="isHovered('copy-' + component.cId) ? 'var(--color-text)' : '#999'"
                              @mouseenter="setHover('copy-' + component.cId)"
                              @mouseleave="clearHover"
                              @click="copyText(component.cId)"></Icon>
                        <Icon v-else
                              key="checkmark"
                              name="checkmark"
                              size="2xl"
                              color="#22c55e"></Icon>
                    </transition>
                </div>
            </div>
        </div>
        <div class="component-information__div">
            <div v-if="component.cCreatedBy" class="single-line"><span class="label">Created by:&ensp;</span>
                <span class="value">{{ component.cCreatedBy }}</span>
            </div>
            <div v-if="lastModified" class="single-line"><span class="label">Last modified:&ensp;</span>
                <span class="value">{{ lastModified }}</span>
            </div>
            <div v-if="component.cModifiedBy" class="single-line"><span class="label">Last modified by:&ensp;</span>
                <span class="value">{{ component.cModifiedBy }}</span>
            </div>
        </div>
        <!--    Instances   -->
        <div class="component-information__div" v-if="uniqueInstances.length !== 0">
            <span class="label">Instances:</span>
            <ul class="instance-list">
                <li v-for="instance in uniqueInstances" :key="instance" class="info-row value">
                    <span>{{ instance }}</span>
                </li>
            </ul>
        </div>
        <!--    Datasets   -->
        <div class="component-information__div" v-if="allDatasetIds?.length > 0">
            <span class="label">Datasets:</span>
            <ul class="dataset-list">
                <li v-for="dataset in allDatasetIds" :key="dataset" class="info-row value">
                    <span>{{ dataset }}</span>
                    <div class="copy-wrapper">
                        <transition name="fade" mode="out-in">
                            <Icon
                                    v-if="copiedId !== dataset"
                                    key="copy"
                                    name="copy"
                                    size="xl"
                                    :color="isHovered('copy-' + dataset) ? 'var(--color-text)' : '#999'"
                                    @mouseenter="setHover('copy-' + dataset)"
                                    @mouseleave="clearHover"
                                    @click="copyText(dataset)"></Icon>
                            <Icon
                                    v-else
                                    key="checkmark"
                                    name="checkmark"
                                    size="2xl"
                                    color="#22c55e"></Icon>
                        </transition>
                    </div>
                </li>
            </ul>
        </div>
        <!--    Layout   -->
        <div class="component-information__div layouts" v-if="component?.positions?.length">
            <span class="label">Layout Information:</span>
            <div v-for="(section, index) in layoutComponents"
                 :key="section.key"
                 :class="{ 'last-item': index === layoutComponents.length - 1 }">
                <ul>{{ section.label }}
                    <li v-for="layoutItem in section.components"
                        :key="layoutItem.id || layoutItem.cId"
                        class="info-row value"
                    >
                        <span>{{ layoutItem.cId }}</span>
                        <div class="copy-wrapper flex align-center">
                            <transition name="fade" mode="out-in">
                                <Icon
                                        v-if="copiedId !== layoutItem.cId"
                                        name="copy"
                                        size="xl"
                                        :color="isHovered('copy-' + layoutItem.cId) ? 'var(--color-text)' : '#999'"
                                        @mouseenter="setHover('copy-' + layoutItem.cId)"
                                        @mouseleave="clearHover"
                                        @click="copyText(layoutItem.cId)"></Icon>
                                <Icon
                                        v-else
                                        key="checkmark"
                                        name="checkmark"
                                        size="2xl"
                                        color="#22c55e"></Icon>
                            </transition>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { library } from "@common/library/library";
import moment from "moment";
import { TooltipPlugin } from '@syncfusion/ej2-vue-popups';
import { GenerateComponent } from "@common/activityStepPanel/GenerateComponent";
import Icon from '@common/icon/Icon.vue';

const editorSettings = {};

const componentInfoCmp = {
    name: "componentInfo",
    editorSettings: editorSettings,
    props: {
        config: Object,
        cId: String
    },
    globalMethods: ['selectComponent'],
    data() {
        return {
            showMore: false,
            copiedId: null,
            hoveredIcon: null
        };
    },
    computed: {
        component() {
            return this?.config;
        },
        componentInformation() {
            return new GenerateComponent(this, this.component?.cInfo, this.cId);
        },
        lastModified() {
            return moment(this.component?.cLastModify).format('MMMM Do YYYY, h:mm a');
        },
        uniqueInstances() {
            const instance = this.component?.instance || [];
            const instances = this.component?.instances || [];
            const allInstances = [...new Set([instance, instances].flat(2))];
            return allInstances.sort();
        },
        layoutComponents() {
            const positions = this?.component?.positions || [];
            return positions.filter((position) => position.components?.length)
                .map((position) => ({
                    label: position.label,
                    key: library.camelCase(position.label),
                    components: position.components || []
                }));
        },
        allDatasetIds() {
            const simplifyId = (str) => {
                if (!str) return null;
                const parts = str.split('___');
                return parts[parts.length - 1];
            };

            const topLevelIds = this.component?.datasetIds?.flatMap((ds) => ds.source?.map(simplifyId) || []) || [];
            const seriesIds = this.component?.countSeries?.flatMap((series) => series.datasetIds?.flatMap((ds) => ds.source?.map(simplifyId) || []) || []) || [];
            const singleId = this.component?.datasetId ? [simplifyId(this.component.datasetId)] : [];

            const allIds = [...topLevelIds, ...seriesIds, ...singleId].filter(Boolean);

            return [...new Set(allIds)];
        },
        showComponentInfo: {
            get() {
                return this.$store.state.componentStore.currentlyOpenInfoPanelId === this.component.cId;
            },
            set(value) {
                this.$emit('info-expanded', value);
                if (value) {
                    this.$store.commit('componentStore/setCurrentlyOpenInfoPanelId', this.component.cId);
                } else {
                    if (this.$store.state.componentStore.currentlyOpenInfoPanelId === this.component.cId) {
                        this.$store.commit('componentStore/setCurrentlyOpenInfoPanelId', null);
                    }
                }
            }
        }
    },
    watch: {
        showMore(val) {
            this.$emit('info-expanded', val);
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        if (this.$store.state.componentStore.currentlyOpenInfoPanelId === this.component.cId) {
            this.$store.commit('componentStore/setCurrentlyOpenInfoPanelId', null);
        }
    },
    methods: {
        copyText(text) {
            library.copyTextToClipboard(text);
            this.copiedId = text;
            clearTimeout(this.copyTimeout);
            this.copyTimeout = setTimeout(() => {
                this.copiedId = null;
            }, 1500);
        },
        toggleShowMore(val) {
            this.$emit('info-expanded', val);
        },
        toggleExpansion() {
            this.showComponentInfo = !this.showComponentInfo;
        },
        handleClickOutside(e) {
            if (this.component.cId) {
                const infoPanelRefName = `infoPanel_${this.component.cId}`;
                const infoIconRefName = `infoIcon_${this.component.cId}`;
                const panel = this.$refs[infoPanelRefName];
                const icon = this.$refs[infoIconRefName];

                if (
                    this.showComponentInfo
                    && panel && !panel.contains(e.target)
                    && icon && !icon.contains(e.target)
                ) {
                    this.showComponentInfo = false; // This will trigger the computed setter to close it
                }
            }
        },
        setHover(id) {
            this.hoveredIcon = id;
        },
        clearHover() {
            this.hoveredIcon = null;
        },
        isHovered(id) {
            return this.hoveredIcon === id;
        }
    },
    components: {
        "ejs-tooltip": TooltipPlugin,
        Icon
    }
};

export { componentInfoCmp };
export default componentInfoCmp;
</script>

<style scoped>
.layout-component.info-expanded > div + div {
    overflow: visible;
}
.component-information__container i.info-icon {
    background: var(--color-surface);
    border-radius: var(--radius-full);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
    position: absolute;
    top: var(--space-2);
    right: var(--space-2);
    z-index: 999;
}
.component-information__container i.info-icon:hover {
    background: #f6f6f6;
    color: #6c6c6c;
}
.component-information {
    position: absolute;
    top: 24px;
    right: 24px;
    height: unset;
    width: clamp(320px, 23vw, 400px);

    background: var(--color-surface);
    box-shadow: var(--shadow-md);
    border: var(--border-default);

    font-family: var(--font-sans), sans-serif;
    font-size: 0.9rem;
    color: var(--color-text);

    transform: scale(0.8) translate(10px, -10px);
    transform-origin: top right;
    opacity: 0;

    transition:
            transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1),
            opacity 0.2s ease;

    pointer-events: none;
    word-break: break-word;

    scrollbar-width: thin; /* Firefox */
    z-index: 9999;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.component-information::-webkit-scrollbar {
    width: 6px;
}
.component-information.expanded {
    transform: scale(1) translate(0, 0);
    opacity: 1;
    pointer-events: auto;
    animation: fadeIn 0.2s ease;
}
.component-information__component-info {
    position: relative;
    top: 0;
    right: 0;
    overflow: visible;
    z-index: 9999;
}
.component-information h3 {
    font-size: var(--text-lg);
    font-weight: var(--text-weight-medium);
    color: #222;
    margin: 0;
}
.component-information .header {
    background: var(--color-background);
    padding: var(--space-2) var(--space-4);
}
/* Close button */
.component-information .header button {
    border: none;
    border-radius: var(--radius-md);
    padding: var(--space-2);
}
.component-information__div {
    border-bottom: var(--border-default);
    padding: var(--space-4);
}
.component-information__div:last-child {
    border-bottom: none;
}
.component-information__div .single-line {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;
    width: 93%;
}
.component-information .component-information__button_div {
    position: absolute;
    right: 0;
    bottom: 0;
}
.component-information__div.description > div {
    padding: 0.5rem;
}
.dataset-list {
    padding: 0;
    margin: 0;
}
.instance-list {
    padding-top: 4px;
    text-transform: lowercase;
}
.info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.info-row span:nth-child(2) {
    min-width: 0; /* IMPORTANT for ellipsis in flex */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.title {
    font-size: var(--text-xl);
    font-weight: var(--text-weight-book);
}
.label {
    cursor: default;
    color: #1c1c1c;
    font-weight: 400;
    padding-right: var(--space-4);
    text-transform: uppercase;
}
.value {
    font-size: var(--text-sm);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.dataset-list .value {
    font-size: 12px;
}
.component-information__div.description .value {
    white-space: unset;
}
.value.cId{
    font-size: var(--text-base);
}
.copy-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    height: 32px;
    transition: opacity 0.2s ease;
}
.copy-wrapper i {
    background: var(--color-surface);
    border-radius: var(--radius-sm);
    cursor: pointer;
    padding: var(--space-2);
    width: auto;
    height: auto;
}
.copy-wrapper i:hover {
    background: var(--color-background);
}
.component-information ul {
    margin: 0;
    padding-inline-start: 0;
}
.component-information li,
.info-row li {
    padding-left: var(--space-4);
}
.component-information__div.layouts ul {
    font-size: 0.95rem;
    border-bottom: 1px solid #ececec;
    padding-top: 8px;
}
.component-information__div.layouts li {
    font-size: 12px;
}
.component-information__div.layouts .last-item ul {
    border-bottom: none;
}
</style>
